/**
 * The RN contract, parsed once and rendered twice.
 *
 * The agreement is authored as markdown and stored in the DB (`ob_items`), not
 * compiled into the app — legal wording changes on counsel's schedule, not on
 * ours, and a wording fix must not require a mobile release. But two very
 * different renderers consume that same text: the React Native reader the nurse
 * scrolls through, and the HTML→PDF template that becomes her signed record.
 *
 * If those two parsed the markdown separately they would drift, and the drift
 * would be invisible: the document she agreed to on screen would stop matching
 * the document filed under her signature. So parsing happens exactly once, here,
 * and both renderers walk the same block list.
 *
 * The dialect is deliberately tiny — headings, paragraphs with bold runs,
 * bullets, italic notes, and two-column tables. That is everything the agreement
 * uses, and a small dialect is one that renders identically in both targets.
 */
/** Rendered in place of a token with no value — reads as an unfilled line. */
export const BLANK_FIELD = "—";
const TOKEN_RE = /\{\{([A-Z_]+)\}\}/g;
/**
 * Substitutes `{{TOKEN}}` placeholders.
 *
 * Applied to the raw markdown BEFORE parsing so a merged value can never be
 * mistaken for markup: a nurse whose legal name contains an asterisk would
 * otherwise turn the rest of a clause bold. Values are inserted as literal text
 * into an already-parsed run, and the PDF renderer escapes them again on the way
 * to HTML.
 *
 * Newlines inside a value are flattened to spaces for the same reason, and it
 * matters more than it sounds: the parser is line-based, so a multi-line value
 * substituted into a table row — a postal address in the parties block — would
 * end that row after its first line and scatter the rest across the document as
 * loose paragraphs. A merge value supplies CONTENT; it must never be able to
 * change the document's STRUCTURE.
 */
export function applyContractMergeFields(markdown, fields) {
    return markdown.replace(TOKEN_RE, (_match, token) => {
        const value = fields[token];
        const flattened = value?.replace(/\s*\r?\n\s*/g, " ").trim();
        return flattened ? flattened : BLANK_FIELD;
    });
}
/** Splits a line into bold / plain runs on `**...**`. */
function parseRuns(line) {
    const runs = [];
    // Non-greedy so `**a** and **b**` yields two bold runs, not one spanning run.
    const re = /\*\*(.+?)\*\*/g;
    let last = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
        if (m.index > last) {
            runs.push({ text: line.slice(last, m.index), bold: false });
        }
        runs.push({ text: m[1], bold: true });
        last = m.index + m[0].length;
    }
    if (last < line.length) {
        runs.push({ text: line.slice(last), bold: false });
    }
    return runs.length ? runs : [{ text: line, bold: false }];
}
/** `| label | value |` → a row. Returns null for a markdown separator row. */
function parseTableRow(line) {
    const cells = line
        .slice(1, line.endsWith("|") ? -1 : undefined)
        .split("|")
        .map((c) => c.trim());
    if (cells.length < 2) {
        return null;
    }
    // `|---|---|` alignment rows carry no content.
    if (cells.every((c) => /^:?-{2,}:?$/.test(c))) {
        return null;
    }
    return { label: cells[0], value: cells.slice(1).join(" ").trim() };
}
/**
 * Markdown → blocks. Consecutive table lines collapse into one `table` block so
 * both renderers can draw a single bordered grid rather than a row at a time.
 */
export function parseContractDoc(markdown) {
    const blocks = [];
    const lines = markdown.replace(/\r\n/g, "\n").split("\n");
    let tableRows = [];
    const flushTable = () => {
        if (tableRows.length) {
            blocks.push({ kind: "table", rows: tableRows });
            tableRows = [];
        }
    };
    for (const raw of lines) {
        const line = raw.trim();
        if (line.startsWith("|")) {
            const row = parseTableRow(line);
            if (row) {
                tableRows.push(row);
            }
            continue;
        }
        flushTable();
        if (!line) {
            continue;
        }
        if (line.startsWith("### ")) {
            blocks.push({ kind: "heading", text: line.slice(4).trim() });
        }
        else if (line.startsWith("## ")) {
            const text = line.slice(3).trim();
            // The one `##` before any `#`-level content is the document's
            // subtitle; everything after is a numbered section heading.
            const seenHeading = blocks.some((b) => b.kind === "heading");
            const seenBody = blocks.some((b) => b.kind === "paragraph" || b.kind === "bullet" || b.kind === "table");
            blocks.push(!seenHeading && !seenBody ? { kind: "subtitle", text } : { kind: "heading", text });
        }
        else if (line.startsWith("# ")) {
            blocks.push({ kind: "title", text: line.slice(2).trim() });
        }
        else if (line.startsWith("- ")) {
            blocks.push({ kind: "bullet", runs: parseRuns(line.slice(2).trim()) });
        }
        else if (/^\*[^*].*\*$/.test(line)) {
            blocks.push({ kind: "note", text: line.slice(1, -1).trim() });
        }
        else {
            blocks.push({ kind: "paragraph", runs: parseRuns(line) });
        }
    }
    flushTable();
    return blocks;
}
export function groupContractSections(blocks) {
    const sections = [];
    let current = { heading: null, blocks: [] };
    for (const block of blocks) {
        if (block.kind === "heading") {
            if (current.blocks.length || current.heading !== null) {
                sections.push(current);
            }
            current = { heading: block.text, blocks: [] };
        }
        else {
            current.blocks.push(block);
        }
    }
    if (current.blocks.length || current.heading !== null) {
        sections.push(current);
    }
    return sections;
}
/** Plain text of a block — used for search, accessibility labels, and tests. */
export function contractBlockText(block) {
    switch (block.kind) {
        case "title":
        case "subtitle":
        case "heading":
        case "note":
            return block.text;
        case "paragraph":
        case "bullet":
            return block.runs.map((r) => r.text).join("");
        case "table":
            return block.rows.map((r) => `${r.label}: ${r.value}`).join("\n");
    }
}
//# sourceMappingURL=contract-doc.js.map